import {
  DESKTOP_SCALING_ABSOLUTE_MIN,
  DESKTOP_SCALING_DESIGN_MAX_WIDTH,
  DESKTOP_SCALING_DESIGN_MIN_WIDTH,
  DESKTOP_SCALING_MIN_SCALE,
  DESKTOP_SCALING_MIN_VIEWPORT,
} from '@/core/constants/appConfig';
import { isWebScalingEnabled } from '@/core/services/webScalingConfigService';

export interface ViewportMetrics {
  /** CSS pixels actually visible (accounts for browser zoom). */
  visibleWidth: number;
  /** CSS pixels RN Web uses for layout / breakpoints (window.innerWidth). */
  layoutWidth: number;
  /** Visible viewport height (visualViewport.height). */
  visibleHeight: number;
  /** Layout height (window.innerHeight). */
  layoutHeight: number;
  /** Browser/page zoom (1 = 100%, 1.25 = 125%). */
  browserZoom: number;
}

/** Epsilon for float comparisons (110%, 125%, 150%, etc.). */
export const VIEWPORT_ZOOM_EPSILON = 0.001;

function resolveBrowserZoom(
  layoutWidth: number,
  visibleWidth: number,
): number {
  if (typeof window === 'undefined') {
    return 1;
  }

  // 1. Mobile pinch zoom — visualViewport.scale is non-1 only for touch/pinch.
  const vpScale = window.visualViewport?.scale;
  if (
    typeof vpScale === 'number' &&
    vpScale > 0 &&
    Math.abs(vpScale - 1) > VIEWPORT_ZOOM_EPSILON
  ) {
    return vpScale;
  }

  // 2. Layout vs visible mismatch (catches some browser implementations).
  if (layoutWidth > 0 && visibleWidth > 0) {
    const widthRatio = layoutWidth / visibleWidth;
    if (Math.abs(widthRatio - 1) > VIEWPORT_ZOOM_EPSILON) {
      return widthRatio;
    }
  }

  // 3. devicePixelRatio-based zoom detection for desktop browsers.
  //
  // On desktop, window.visualViewport.scale is always 1 even when the user
  // has set the browser zoom to e.g. 125 %. innerWidth and visualViewport.width
  // are both reduced by the same factor, so signal #2 above never fires either.
  //
  // DPR = baseDPR × browserZoom.
  // Using Math.floor(DPR) as the baseDPR estimate:
  //   1× screen at 125 % → DPR = 1.25, baseDPR = 1, zoom = 1.25 ✓
  //   2× HiDPI at 125 %  → DPR = 2.50, baseDPR = 2, zoom = 1.25 ✓
  //   1× screen at 100 % → DPR = 1.00, no fractional part       ✓
  //   2× HiDPI at 100 %  → DPR = 2.00, no fractional part       ✓
  const dpr = window.devicePixelRatio ?? 1;
  const baseDpr = Math.floor(dpr);
  if (baseDpr >= 1 && dpr > baseDpr + VIEWPORT_ZOOM_EPSILON) {
    return dpr / baseDpr;
  }

  return 1;
}

/** Read current viewport metrics (sync). Web only — native callers should use Dimensions. */
export function readViewportMetrics(): ViewportMetrics {
  if (typeof window === 'undefined') {
    return {
      visibleWidth: DESKTOP_SCALING_DESIGN_MAX_WIDTH, // SSR fallback
      layoutWidth: DESKTOP_SCALING_DESIGN_MAX_WIDTH,
      visibleHeight: 800,
      layoutHeight: 800,
      browserZoom: 1,
    };
  }

  const layoutWidth = window.innerWidth;
  const layoutHeight = window.innerHeight;
  const visualWidth = window.visualViewport?.width;
  const visualHeight = window.visualViewport?.height;
  const visibleWidth =
    typeof visualWidth === 'number' && visualWidth > 0
      ? visualWidth
      : layoutWidth;
  const visibleHeight =
    typeof visualHeight === 'number' && visualHeight > 0
      ? visualHeight
      : layoutHeight;
  const browserZoom = resolveBrowserZoom(layoutWidth, visibleWidth);

  return {
    visibleWidth,
    layoutWidth,
    visibleHeight,
    layoutHeight,
    browserZoom,
  };
}

/** Width for layout breakpoint decisions on web (visible CSS px). */
export function readBreakpointWidth(): number {
  return readViewportMetrics().visibleWidth;
}

/** Compensate for any browser zoom IN. Never upscale when zoomed out. */
export function computeBrowserZoomScale(browserZoom: number): number {
  if (browserZoom <= 1 + VIEWPORT_ZOOM_EPSILON) {
    return 1;
  }
  return 1 / browserZoom;
}

/** Design-scale from Figma rem ratio (fluid 0.8 → 1.0 between 1512–1890 px). */
export function computeDesignViewportScale(visibleWidth: number): number {
  if (visibleWidth >= DESKTOP_SCALING_DESIGN_MAX_WIDTH) {
    return 1;
  }

  if (visibleWidth <= DESKTOP_SCALING_DESIGN_MIN_WIDTH) {
    const ratio = visibleWidth / DESKTOP_SCALING_DESIGN_MIN_WIDTH;
    return Math.max(
      DESKTOP_SCALING_MIN_SCALE,
      DESKTOP_SCALING_MIN_SCALE * ratio,
    );
  }

  const range =
    DESKTOP_SCALING_DESIGN_MAX_WIDTH - DESKTOP_SCALING_DESIGN_MIN_WIDTH;
  const progress =
    (visibleWidth - DESKTOP_SCALING_DESIGN_MIN_WIDTH) / range;
  return DESKTOP_SCALING_MIN_SCALE + progress * (1 - DESKTOP_SCALING_MIN_SCALE);
}

/**
 * Combined fit-scale for global CSS zoom:
 * design curve + browser zoom + layout/visible mismatch.
 *
 * The design curve (computeDesignViewportScale) is the primary driver: at 125%
 * browser zoom the visible viewport width drops to ~1512 px which maps to 0.8,
 * counteracting the zoom so content reads at its intended size.  The browser-zoom
 * and mismatch terms provide additional safety for extreme zoom levels.
 *
 * NOTE: The Universal hub / space pages depend on --bloom-100dvh and
 * breakpointHeight from WebViewportProvider to compensate for this CSS zoom —
 * those fixes must stay in place (see GlobalWebStyles and WebViewportProvider).
 */
export function computeDesktopFitScale(metrics: ViewportMetrics): number {
  if (!isWebScalingEnabled()) {
    return 1;
  }

  const { visibleWidth, layoutWidth, browserZoom } = metrics;

  if (visibleWidth < DESKTOP_SCALING_MIN_VIEWPORT || visibleWidth <= 0 || layoutWidth <= 0) {
    return 1;
  }

  const designScale = computeDesignViewportScale(visibleWidth);
  const browserZoomScale = computeBrowserZoomScale(browserZoom);
  const layoutMismatchScale = Math.min(1, visibleWidth / layoutWidth);

  const rawScale = Math.min(
    designScale,
    browserZoomScale,
    layoutMismatchScale,
    1,
  );
  return Math.max(DESKTOP_SCALING_ABSOLUTE_MIN, rawScale);
}

/**
 * Convert measureInWindow / getBoundingClientRect pixels into layout pixels
 * under `html { zoom }`. Absolute Modal/`position` styles use the pre-zoom
 * coordinate space; uncorrected visual coords make anchored menus drift left.
 */
export function visualPxToLayoutPx(visualPx: number): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return visualPx;
  }

  const cssVar = Number.parseFloat(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--bloom-desktop-scale')
      .trim(),
  );
  const scale =
    Number.isFinite(cssVar) && cssVar > 0
      ? cssVar
      : computeDesktopFitScale(readViewportMetrics());

  if (scale >= 1 - VIEWPORT_ZOOM_EPSILON) {
    return visualPx;
  }
  return visualPx / scale;
}

export function subscribeViewportChanges(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handler = () => {
    onChange();
  };

  window.addEventListener('resize', handler);
  window.addEventListener('orientationchange', handler);
  window.visualViewport?.addEventListener('resize', handler);
  window.visualViewport?.addEventListener('scroll', handler);

  return () => {
    window.removeEventListener('resize', handler);
    window.removeEventListener('orientationchange', handler);
    window.visualViewport?.removeEventListener('resize', handler);
    window.visualViewport?.removeEventListener('scroll', handler);
  };
}
